/* ===========================================================
   MegaJimny — Shortcode System
   Shortcodes:
     [collection-carousel="collection-handle"]
     [product-feature="product-handle"]
     [quotation="quote text here" author="Nicholas Duarte"]
     [tip title="Tip Title" text="Tip content here"]
   - Collection: title (top), price (bottom-left), "View Product" button (bottom-right)
   - Feature: image left, details right, variant pills, CTA overlay on image
   - Quotation: Styled quote block with author attribution
   - Tip: Highlighted tip/trick box with title and content
   - Robust to late-injected article content; waits for Swiper
   =========================================================== */

(function () {
  // Cover common article wrappers (native + Elementor + rte-formatter)
  const ARTICLE_SELECTOR =
    '.article-template, .blog__article__content.rte, .article__content-inner, .article-template__content, .article-template__content.rte, .article .rte, .article-content, .rte, .elementor-widget-theme-post-content, rte-formatter';

  const HAS_SHORTCODE_RE = /\[(?:collection-carousel|product-feature|quotation|tip)[\s=][^\]]*\]/;

  ready(initWithObserver);

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
    window.addEventListener('load', fn, { once: true });
  }

  function initWithObserver() {
    processAll();

    // Observe for late injections (Elementor/sections)
    if (document.body && HAS_SHORTCODE_RE.test(document.body.textContent || '')) {
      const mo = new MutationObserver(() => {
        const changed = processAll();
        if (!HAS_SHORTCODE_RE.test(document.body.textContent || '')) mo.disconnect();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
  }

  function processAll() {
    let didSomething = false;
    const containers = document.querySelectorAll(ARTICLE_SELECTOR);
    containers.forEach(container => {
      const html = container.innerHTML;
      if (!HAS_SHORTCODE_RE.test(html)) return;

      let replaced = html;

      // Insert collection carousels
      let cIdx = 0;
      replaced = replaced.replace(/\[collection-carousel="([^"]+)"\]/g, (_, handle) => {
        didSomething = true;
        const id = `mj-carousel-${handle}-${cIdx++}`;
        return `
          <section id="${id}" class="mj-slider-wrapper mj-slider" data-collection="${handle}">
            <div class="swiper-container"><div class="swiper-wrapper"></div></div>
          </section>`;
      });

      // Insert single product features
      let pIdx = 0;
      replaced = replaced.replace(/\[product-feature="([^"]+)"\]/g, (_, handle) => {
        didSomething = true;
        const id = `mj-feature-${handle}-${pIdx++}`;
        return `
          <section id="${id}" class="mj-feature-wrapper" data-product="${handle}">
            <div class="mj-feature">
              <div class="mj-feature__media">
                <div class="mj-feature__img mj-skel"></div>
                <div class="mj-feature__cta-overlay">
                  <a class="mj-btn mj-btn--ghost mj-feature__view" href="#" target="_self" rel="noopener">View Product</a>
                  <form class="mj-feature__purchase" action="/cart/add" method="post">
                    <input type="hidden" name="id" value="">
                    <button type="submit" class="mj-btn mj-btn--primary">Add To Cart</button>
                  </form>
                </div>
              </div>
              <div class="mj-feature__details">
                <h2 class="mj-feature__title mj-skel">Loading…</h2>
                <div class="mj-feature__desc mj-skel"></div>
                <div class="mj-feature__price-row">
                  <span class="mj-feature__price mj-skel">$—</span>
                </div>
                <div class="mj-feature__variant-label">Variant</div>
                <div class="mj-feature__variant-grid" role="listbox" aria-label="Select variant"></div>
              </div>
            </div>
          </section>`;
      });

      // Insert quotation blocks
      let qIdx = 0;
      replaced = replaced.replace(/\[quotation="([^"]+)"(?:\s+author="([^"]*)")?\]/g, (_, quote, author) => {
        didSomething = true;
        const id = `mj-quote-${qIdx++}`;
        const authorText = author || 'Nicholas Duarte';
        return `
          <section id="${id}" class="mj-quotation-wrapper">
            <div class="mj-quotation">
              <div class="mj-quotation__icon">
                <svg width="40" height="32" viewBox="0 0 40 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 32V18.6667C0 13.4222 1.33333 8.88889 4 5.06667C6.66667 1.24444 10.6667 0 16 0V5.33333C13.7778 5.33333 11.8889 6.13333 10.3333 7.73333C8.77778 9.33333 8 11.3333 8 13.7333V16H16V32H0ZM24 32V18.6667C24 13.4222 25.3333 8.88889 28 5.06667C30.6667 1.24444 34.6667 0 40 0V5.33333C37.7778 5.33333 35.8889 6.13333 34.3333 7.73333C32.7778 9.33333 32 11.3333 32 13.7333V16H40V32H24Z" fill="currentColor"/>
                </svg>
              </div>
              <blockquote class="mj-quotation__text">${escapeHtml(quote)}</blockquote>
              <div class="mj-quotation__author">— ${escapeHtml(authorText)}</div>
            </div>
          </section>`;
      });

      // Insert tip blocks
      let tIdx = 0;
      replaced = replaced.replace(/\[tip\s+title="([^"]+)"\s+text="([^"]+)"\]/g, (_, title, text) => {
        didSomething = true;
        const id = `mj-tip-${tIdx++}`;
        return `
          <section id="${id}" class="mj-tip-wrapper">
            <div class="mj-tip">
              <div class="mj-tip__icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor"/>
                </svg>
              </div>
              <div class="mj-tip__content">
                <h3 class="mj-tip__title">${escapeHtml(title)}</h3>
                <p class="mj-tip__text">${escapeHtml(text)}</p>
              </div>
            </div>
          </section>`;
      });

      if (replaced !== html) container.innerHTML = replaced;
    });

    buildAll();
    return didSomething;
  }

  function buildAll() {
    document.querySelectorAll('.mj-slider-wrapper[data-collection]').forEach(buildCollectionCarousel);
    document.querySelectorAll('.mj-feature-wrapper[data-product]').forEach(buildProductFeature);
  }

  /* ===================== COLLECTIONS ===================== */
  function buildCollectionCarousel(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle   = wrapper.getAttribute('data-collection');
    const trackEl  = wrapper.querySelector('.swiper-wrapper');
    const swiperEl = wrapper.querySelector('.swiper-container');

    fetch(`/collections/${handle}/products.json?limit=12`)
      .then(r => r.json())
      .then(json => {
        (json.products || []).forEach(product => {
          const variant = product.variants && product.variants[0];
          if (!variant) return;

          const imgSrc   = getPreferredCollectionImage(product.images);
          const priceTxt = formatPrice(variant.price);

          const slide = document.createElement('div');
          slide.className = 'swiper-slide';
          if (imgSrc) slide.style.backgroundImage = `url(${imgSrc})`;

          slide.innerHTML = `
            <div class="product-name">${escapeHtml(product.title)}</div>
            <div class="product-price">${priceTxt}</div>
            <a class="mj-slide-link" href="/products/${product.handle}" aria-label="View ${escapeHtml(product.title)}"></a>
            <a class="mj-slide-btn" href="/products/${product.handle}" aria-label="View ${escapeHtml(product.title)}"><img src="https://cdn.shopify.com/s/files/1/0677/2542/6987/files/view.png?v=1764721055" alt="View"></a>
          `;
          trackEl.appendChild(slide);
        });

        waitForSwiper(() => {
          new Swiper(swiperEl, {
            freeMode: true,
            slidesPerView: 'auto',
            spaceBetween: 12,
            loop: false,
            navigation: false,
            pagination: false
          });
        });
      })
      .catch(err => console.error('MegaJimny collection carousel error:', err));
  }

  /* ===================== PRODUCT FEATURE ===================== */
  function buildProductFeature(wrapper) {
    if (wrapper.__built) return;
    wrapper.__built = true;

    const handle  = wrapper.getAttribute('data-product');
    const card    = wrapper.querySelector('.mj-feature');

    const imgBox  = card.querySelector('.mj-feature__img');
    const titleEl = card.querySelector('.mj-feature__title');
    const descEl  = card.querySelector('.mj-feature__desc');
    const priceEl = card.querySelector('.mj-feature__price');
    const gridEl  = card.querySelector('.mj-feature__variant-grid');
    const labelEl = card.querySelector('.mj-feature__variant-label');
    const form    = card.querySelector('.mj-feature__purchase');
    const idInput = form.querySelector('input[name="id"]');
    const viewA   = card.querySelector('.mj-feature__view');

    viewA.href = `/products/${handle}`;

    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(prod => {
        if (!prod || !Array.isArray(prod.variants) || prod.variants.length === 0) {
          titleEl.textContent = 'Product unavailable';
          card.classList.add('mj-feature--error');
          return;
        }

        // ---- robust image from /products/{handle}.js (strings) ----
        const heroImg =
          (Array.isArray(prod.images) && (prod.images[1] || prod.images[0])) ||
          prod.featured_image || '';
        if (heroImg) {
          imgBox.classList.remove('mj-skel');
          imgBox.style.backgroundImage = `url("${heroImg}")`;
        }

        // Title & description
        titleEl.classList.remove('mj-skel');
        titleEl.textContent = prod.title;

        const clean = stripHtml(prod.description || '');
        const short = clean.length > 260 ? clean.slice(0, 257) + '…' : clean;
        descEl.classList.remove('mj-skel');
        descEl.textContent = short;

        // Variants
        const variants = prod.variants;
        const firstVar = variants[0];
        idInput.value  = firstVar.id;
        priceEl.classList.remove('mj-skel');
        priceEl.textContent = formatPrice(firstVar.price);

        const singleDefault =
          variants.length === 1 &&
          (firstVar.title === 'Default Title' || firstVar.title === 'Default');

        if (singleDefault) {
          // hide selector if useless
          if (labelEl) labelEl.style.display = 'none';
          if (gridEl)  gridEl.style.display  = 'none';
          // more compact image if only default variant
          card.classList.add('mj-feature--compact');
          return;
        }

        // pill buttons for variants
        const btns = variants.map(v => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'mj-var-btn';
          b.textContent = v.title;
          b.dataset.id = v.id;
          b.dataset.price = v.price;
          b.setAttribute('role', 'option');
          b.setAttribute('aria-label', `Select ${v.title}`);
          return b;
        });

        btns.forEach(b => gridEl.appendChild(b));

        function selectVariant(btn) {
          btns.forEach(x => x.classList.remove('is-active'));
          btn.classList.add('is-active');
          idInput.value = btn.dataset.id;
          priceEl.textContent = formatPrice(btn.dataset.price);
        }
        selectVariant(btns[0]);
        btns.forEach(b => b.addEventListener('click', () => selectVariant(b)));
      })
      .catch(err => {
        titleEl.textContent = 'Error loading product';
        console.error('MegaJimny product feature error:', err);
      });
  }

  /* ===================== helpers ===================== */

  function waitForSwiper(cb) {
    if (window.Swiper) return cb();
    const iv = setInterval(() => { if (window.Swiper) { clearInterval(iv); cb(); } }, 50);
    setTimeout(() => clearInterval(iv), 3000);
  }

  // collections endpoint -> [{src:"..."}]
  function getPreferredCollectionImage(images) {
    if (!images || !images.length) return '';
    const first  = images[0] && images[0].src;
    const second = images[1] && images[1].src;
    return first || second || '';
  }

  function formatPrice(raw) {
    let dollars;
    if (typeof raw === 'number') dollars = raw / 100;
    else {
      const s = String(raw).trim();
      if (/^\d+$/.test(s)) dollars = parseInt(s, 10) / 100;
      else if (/^\d+(\.\d+)?$/.test(s)) dollars = parseFloat(s);
      else dollars = 0;
    }
    return dollars.toLocaleString(undefined, {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
})();